import { Trans } from '@lingui/macro'
import { formatNumber, NumberType } from '@x-swap-protocol/conedison/format'
import { ChainId } from '@xspswap/smart-order-router'
import {
  LARGE_MEDIA_BREAKPOINT,
  MAX_WIDTH_MEDIA_BREAKPOINT,
  MEDIUM_MEDIA_BREAKPOINT,
  SMALL_MEDIA_BREAKPOINT,
} from 'components/Tokens/constants'
import { LoadingBubble } from 'components/Tokens/loading'
import { MouseoverTooltip } from 'components/Tooltip'
import { format } from 'date-fns'
import { PoolAndPairQuery } from 'graphql/competition/__generated__/types-and-hooks'
import { getTokenDetailsURL } from 'graphql/data/util'
import { useAtomValue } from 'jotai/utils'
import { darken } from 'polished'
import { CSSProperties, ForwardedRef, forwardRef, ReactNode, useMemo } from 'react'
import { ArrowDown, ArrowUp, Info } from 'react-feather'
import { Link } from 'react-router-dom'
import styled, { css, keyframes, useTheme } from 'styled-components/macro'
import { ClickableStyle } from 'theme'
import { getNowSeconds } from 'utils/getNowSeconds'

import CurrencyLogo from '../../../components/Logo/CurrencyLogo'
import { UserTradeT } from '../../../graphql/competition/UserTrades'
import { Chain } from '../../../graphql/data/__generated__/types-and-hooks'
import { SortableCategories, sortAscendingAtom, sortMethodAtom, useSetSortMethod } from './state'
import { TokenSortMethodCompetition } from './state'

const Cell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`
const StyledTokenRow = styled.div<{
  first?: boolean
  last?: boolean
  $loading?: boolean
}>`
  background-color: transparent;
  display: grid;
  font-size: 16px;
  grid-template-columns: 1fr 12fr 3fr 4fr 3fr 4fr;
  line-height: 24px;
  max-width: ${MAX_WIDTH_MEDIA_BREAKPOINT};
  min-width: 390px;
  ${({ first, last }) => css`
    height: ${first || last ? '72px' : '64px'};
    padding-top: ${first ? '8px' : '0px'};
    padding-bottom: ${last ? '8px' : '0px'};
  `}
  padding-left: 12px;
  padding-right: 12px;
  transition: ${({
    theme: {
      transition: { duration, timing },
    },
  }) => css`background-color ${duration.medium} ${timing.ease}`};
  width: 100%;
  transition-duration: ${({ theme }) => theme.transition.duration.fast};

  &:hover {
    ${({ $loading, theme }) =>
      !$loading &&
      css`
        background-color: ${theme.hoverDefault};
      `}
    ${({ last }) =>
      last &&
      css`
        border-radius: 0px 0px 8px 8px;
      `}
  }

  @media only screen and (max-width: ${MAX_WIDTH_MEDIA_BREAKPOINT}) {
    grid-template-columns: 1fr 11fr 4.5fr 4.5fr 4.5fr 1.7fr;
  }

  @media only screen and (max-width: ${LARGE_MEDIA_BREAKPOINT}) {
    grid-template-columns: 1fr 12fr 4.5fr 4.5fr 1.7fr;
  }

  @media only screen and (max-width: ${MEDIUM_MEDIA_BREAKPOINT}) {
    grid-template-columns: 1fr 15fr 5fr 1.2fr;
  }

  @media only screen and (max-width: ${SMALL_MEDIA_BREAKPOINT}) {
    grid-template-columns: 2fr 3fr;
    min-width: unset;
    border-bottom: 0.5px solid ${({ theme }) => theme.backgroundModule};

    :last-of-type {
      border-bottom: none;
    }
  }
`

const ClickableContent = styled.div`
  display: flex;
  text-decoration: none;
  color: ${({ theme }) => theme.textPrimary};
  align-items: center;
  cursor: pointer;
`
const ClickableName = styled(ClickableContent)`
  gap: 8px;
  max-width: 100%;
`
const StyledHeaderRow = styled(StyledTokenRow)`
  border-bottom: 1px solid;
  border-color: ${({ theme }) => theme.backgroundOutline};
  border-radius: 8px 8px 0px 0px;
  color: ${({ theme }) => theme.textTertiary};
  font-size: 14px;
  height: 48px;
  line-height: 16px;
  padding: 0px 12px;
  width: 100%;
  justify-content: center;

  &:hover {
    background-color: transparent;
  }

  @media only screen and (max-width: ${SMALL_MEDIA_BREAKPOINT}) {
    justify-content: space-between;
  }
`

const ListNumberCell = styled(Cell)<{ header: boolean }>`
  color: ${({ theme }) => theme.textTertiary};
  min-width: 32px;
  font-size: 14px;

  @media only screen and (max-width: ${SMALL_MEDIA_BREAKPOINT}) {
    display: none;
  }
`
const DataCell = styled(Cell)<{ sortable: boolean }>`
  justify-content: flex-end;
  min-width: 80px;
  user-select: ${({ sortable }) => (sortable ? 'none' : 'unset')};
  transition: ${({
    theme: {
      transition: { duration, timing },
    },
  }) => css`background-color ${duration.medium} ${timing.ease}`};
`
const TvlCell = styled(DataCell)`
  padding-right: 8px;
  justify-content: center;
  @media only screen and (max-width: 368px) {
    display: none;
  }
`
const NameCell = styled(Cell)`
  justify-content: flex-start;
  padding: 0px 8px;
  min-width: 240px;
  gap: 8px;
`

const PriceCell = styled(DataCell)`
  padding-right: 8px;
  justify-content: center;
  @media only screen and (max-width: ${MEDIUM_MEDIA_BREAKPOINT}) {
    display: none;
  }
`
const PercentChangeCell = styled(DataCell)`
  padding-right: 8px;
  justify-content: center;
  @media only screen and (max-width: ${SMALL_MEDIA_BREAKPOINT}) {
    display: none;
  }
`
const PercentChangeInfoCell = styled(Cell)`
  display: none;
  justify-content: center;

  @media only screen and (max-width: ${SMALL_MEDIA_BREAKPOINT}) {
    display: flex;
    justify-content: flex-end;
    color: ${({ theme }) => theme.textTertiary};
    font-size: 12px;
    line-height: 16px;
  }
`
const PriceInfoCell = styled(Cell)`
  justify-content: center;
  flex: 1;

  @media only screen and (max-width: ${SMALL_MEDIA_BREAKPOINT}) {
    flex-direction: column;
    align-items: flex-end;
  }
`

const SymbolCell = styled(DataCell)`
  padding-right: 8px;
  justify-content: center;
  @media only screen and (max-width: ${SMALL_MEDIA_BREAKPOINT}) {
    display: none;
  }
`

const HeaderCellWrapper = styled.span<{
  onClick?: () => void
  textAlign?: 'left' | 'right' | 'center'
  sortable?: boolean
}>`
  align-items: center;
  cursor: ${({ onClick, sortable }) => (sortable && onClick ? 'pointer' : 'unset')};
  display: flex;
  gap: 4px;
  justify-content: ${({ textAlign }) =>
    textAlign === 'left' ? 'flex-start' : textAlign === 'right' ? 'flex-end' : 'center'};
  width: 100%;
  &:hover {
    ${({ sortable }) => sortable && ClickableStyle}
  }
`
// const SparkLineCell = styled(Cell)`
//   padding: 0px 24px;
//   min-width: 120px;

//   @media only screen and (max-width: ${MAX_WIDTH_MEDIA_BREAKPOINT}) {
//     display: none;
//   }
// `
// const SparkLine = styled(Cell)`
//   width: 124px;
//   height: 42px;
// `
const StyledLink = styled(Link)`
  text-decoration: none;
`
const TokenInfoCell = styled(Cell)`
  gap: 8px;
  line-height: 24px;
  font-size: 16px;
  max-width: inherit;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media only screen and (max-width: ${SMALL_MEDIA_BREAKPOINT}) {
    justify-content: flex-start;
    flex-direction: column;
    gap: 0px;
    width: max-content;
    font-weight: 500;
  }
`
const TokenName = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
`
const TokenSymbolMobile = styled(Cell)`
  display: none;
  color: ${({ theme }) => theme.textTertiary};
  text-transform: uppercase;

  @media only screen and (max-width: ${SMALL_MEDIA_BREAKPOINT}) {
    display: flex;
    font-size: 12px;
    height: 16px;
    justify-content: flex-start;
    width: 100%;
  }
`

const TokenSymbolDesktop = styled.span`
  color: ${({ theme }) => theme.textTertiary};
  text-transform: uppercase;
  @media only screen and (max-width: ${SMALL_MEDIA_BREAKPOINT}) {
    display: none;
    /* font-size: 12px;
    height: 16px;
    justify-content: flex-start;
    width: 100%; */
  }
`
const VolumeCell = styled(DataCell)`
  padding-right: 8px;
  min-width: 135px;
  justify-content: center;
  @media only screen and (max-width: ${LARGE_MEDIA_BREAKPOINT}) {
    display: none;
  }
`
const SmallLoadingBubble = styled(LoadingBubble)`
  width: 25%;
`
const MediumLoadingBubble = styled(LoadingBubble)`
  width: 65%;
`
const LongLoadingBubble = styled(LoadingBubble)`
  width: 90%;
`
const IconLoadingBubble = styled(LoadingBubble)`
  border-radius: 50%;
  width: 24px;
`

const InfoIconContainer = styled.div`
  margin-left: 2px;
  display: flex;
  align-items: center;
  cursor: help;
`

export const blinkAnimation = keyframes`
  100% {
    box-shadow: none;
  }
`

const MultiplierWrapper = styled.div`
  background: ${({ theme }) => theme.accentCritical};
  box-shadow: 0 0 4px 1px ${({ theme }) => darken(0.09, theme.accentCritical)};
  padding: 0 6px;
  height: 15px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 8px;
  font-size: 13px;
  border-radius: 8px;
  font-weight: bold;
  animation: ${blinkAnimation} 1.5s infinite;
`

const HEADER_DESCRIPTIONS: Record<TokenSortMethodCompetition, ReactNode | undefined> = {
  [TokenSortMethodCompetition.TRADES_COUNT]: undefined,
  [TokenSortMethodCompetition.Volume]: <Trans>Your trade volume for the token</Trans>,
  [TokenSortMethodCompetition.SYMBOL]: undefined,
  [TokenSortMethodCompetition.NAME]: undefined,
  [TokenSortMethodCompetition.Score]: <Trans>Your score for the token(includes bonuses)</Trans>,
  [TokenSortMethodCompetition.Score24]: undefined,
}

/* Get singular header cell for header row */
function HeaderCell({
  category,
  textAlign = 'right',
}: {
  category: TokenSortMethodCompetition // TODO: change this to make it work for trans
  textAlign?: 'left' | 'right' | 'center'
}) {
  const theme = useTheme()
  const sortAscending = useAtomValue(sortAscendingAtom)
  //@ts-ignore
  const handleSortCategory = useSetSortMethod(category)
  const sortMethod = useAtomValue(sortMethodAtom)
  const sortable = SortableCategories[category]

  const description = HEADER_DESCRIPTIONS[category]

  return (
    <HeaderCellWrapper sortable={sortable} onClick={sortable ? handleSortCategory : () => null} textAlign={textAlign}>
      {/* @ts-ignore */}
      {sortable && sortMethod === category && (
        <>
          {sortAscending ? (
            <ArrowUp size={20} strokeWidth={1.8} color={theme.accentActive} />
          ) : (
            <ArrowDown size={20} strokeWidth={1.8} color={theme.accentActive} />
          )}
        </>
      )}
      {category}
      {description && (
        <MouseoverTooltip text={description} placement="top">
          <InfoIconContainer>
            <Info size={14} />
          </InfoIconContainer>
        </MouseoverTooltip>
      )}
    </HeaderCellWrapper>
  )
}

/* Token Row: skeleton row component */
function TokenRow({
  header,
  listNumber,
  tokenInfo,
  // symbol,
  price,
  percentChange,
  tvl,
  volume,
  // sparkLine,
  ...rest
}: {
  first?: boolean
  header: boolean
  listNumber?: ReactNode
  loading?: boolean
  tvl: ReactNode
  price: ReactNode
  percentChange: ReactNode
  // sparkLine?: ReactNode
  tokenInfo: ReactNode
  // symbol?: ReactNode
  volume: ReactNode
  last?: boolean
  style?: CSSProperties
}) {
  const rowCells = (
    <>
      <ListNumberCell header={header}>{listNumber}</ListNumberCell>
      <NameCell data-testid="name-cell">{tokenInfo}</NameCell>
      {/* <SymbolCell data-testid="symbol-cell" sortable={header}>
        {symbol}
      </SymbolCell> */}
      <VolumeCell data-testid="volume-cell" sortable={header}>
        {volume}
      </VolumeCell>
      <PriceCell data-testid="price-cell" sortable={header}>
        {price}
      </PriceCell>
      <PercentChangeCell data-testid="percent-change-cell" sortable={header}>
        {percentChange}
      </PercentChangeCell>
      <TvlCell data-testid="tvl-cell" sortable={header}>
        {tvl}
      </TvlCell>
      {/* <SparkLineCell>{sparkLine}</SparkLineCell> */}
    </>
  )
  if (header) return <StyledHeaderRow data-testid="header-row">{rowCells}</StyledHeaderRow>
  return <StyledTokenRow {...rest}>{rowCells}</StyledTokenRow>
}

/* Header Row: top header row component for table */
export function HeaderRow() {
  return (
    <TokenRow
      header={true}
      listNumber="#"
      tokenInfo={<HeaderCell category={TokenSortMethodCompetition.NAME} textAlign="left" />}
      // symbol={<HeaderCell category={TokenSortMethodCompetition.SYMBOL} textAlign="center" />}
      tvl={<HeaderCell category={TokenSortMethodCompetition.Score} textAlign="center" />}
      volume={<HeaderCell category={TokenSortMethodCompetition.Score24} textAlign="center" />}
      price={<HeaderCell category={TokenSortMethodCompetition.TRADES_COUNT} textAlign="center" />}
      percentChange={<HeaderCell category={TokenSortMethodCompetition.Volume} textAlign="center" />}
      // sparkLine={null}
    />
  )
}

/* Loading State: row component with loading bubbles */
export function LoadingRow(props: { first?: boolean; last?: boolean }) {
  return (
    <TokenRow
      header={false}
      listNumber={<SmallLoadingBubble />}
      tokenInfo={
        <>
          <IconLoadingBubble />
          <MediumLoadingBubble />
        </>
      }
      // symbol={<LoadingBubble />}
      price={<MediumLoadingBubble />}
      percentChange={<LoadingBubble />}
      tvl={<LoadingBubble />}
      volume={<LoadingBubble />}
      // sparkLine={<SparkLineLoadingBubble />}
      {...props}
    />
  )
}

interface LoadedRowProps {
  tokenListIndex: number
  tokenListLength: number
  totalTrade: UserTradeT & { score24: number }
  // sparklineMap: SparklineMap
  sortRank: number
  multiplierData: PoolAndPairQuery['tokens'][number]
}

/* Loaded State: row component with token information */
export const LoadedRow = forwardRef(
  (
    {
      tokenListIndex,
      tokenListLength,
      totalTrade: { token, bonusUsdVolume, txCount, clearTokenVolume, clearUsdVolume, score24, totalUsdVolume },
      sortRank,
      multiplierData: { id, symbol, name, multiplier, multiplierStart, multiplierEnd },
    }: LoadedRowProps,
    ref: ForwardedRef<HTMLDivElement>
  ) => {
    const multi = useMemo(() => {
      const secs = getNowSeconds()
      let start,
        end = 0
      let m = 100
      try {
        start = Number(multiplierStart)
        end = Number(multiplierEnd)
        m = Number(multiplier)
      } catch (e) {
        console.log(e)
        return null
      }
      if (m > 100 && secs > start && secs < end) {
        return (
          <MouseoverTooltip
            text={
              <div>
                <div style={{ fontSize: '15px', marginBottom: '10px' }}>Token bonus multiplier</div>
                <div>Start: {format(multiplierStart * 1000, 'dd MMM yyyy HH:mm')}UTC</div>
                <div>End: {format(multiplierEnd * 1000, 'dd MMM yyyy HH:mm')}UTC</div>
              </div>
            }
            placement="top"
          >
            <MultiplierWrapper>x {m / 100}</MultiplierWrapper>
          </MouseoverTooltip>
        )
      }
      return null
    }, [multiplier, multiplierStart, multiplierEnd])
    return (
      <div ref={ref} data-testid={`token-table-row-${token.id}`}>
        <StyledLink
          to={getTokenDetailsURL({ address: token.id, chain: Chain.Xinfin })}
          // onClick={() =>
          //   sendAnalyticsEvent(InterfaceEventName.EXPLORE_TOKEN_ROW_CLICKED, exploreTokenSelectedEventProperties)
          // }
        >
          <TokenRow
            header={false}
            listNumber={sortRank}
            tokenInfo={
              <ClickableName>
                <CurrencyLogo
                  currency={{
                    chainId: ChainId.XDC,
                    symbol: token.symbol,
                    decimals: 18,
                    name: token.name,
                    // @ts-ignore
                    wrapped: {
                      address: token.id,
                    },
                    address: token.id,
                    isNative: false,
                    isToken: true,
                  }}
                />
                <TokenInfoCell>
                  <TokenName data-cy="token-name">
                    {token.name} <TokenSymbolDesktop>({token.symbol})</TokenSymbolDesktop>
                  </TokenName>
                  <TokenSymbolMobile>{token.symbol}</TokenSymbolMobile>
                </TokenInfoCell>
                {multi}
              </ClickableName>
            }
            price={<ClickableContent>{txCount}</ClickableContent>}
            percentChange={
              <ClickableContent>
                {`${formatNumber(parseFloat(clearUsdVolume), NumberType.FiatTokenStats)}`}
              </ClickableContent>
            }
            tvl={<ClickableContent>{`${formatNumber(totalUsdVolume)}`}</ClickableContent>}
            volume={<ClickableContent>{`${formatNumber(parseFloat(String(score24)))}`}</ClickableContent>}
            first={tokenListIndex === 0}
            last={tokenListIndex === tokenListLength - 1}
          />
        </StyledLink>
      </div>
    )
  }
)

LoadedRow.displayName = 'LoadedRow'
